import { IS_IFRAME, requestPostFetch } from '@repo/common-utils';

import { AirbridgeService } from './services/AirbridgeService';
import { DatadogService } from './services/DatadogService';
import { GoogleAnalyticsService } from './services/GoogleAnalyticsService';
import { KakaoPixelService } from './services/KakaoPixelService';
import { MetaPixelService } from './services/MetaPixelService';
import { MixpanelService } from './services/MixpanelService';
import { NaverPixelService } from './services/NaverPixelService';
import { TiktokPixelService } from './services/TiktokPixelService';
import {
  SendEventProperties,
  SetUserParams,
  EventTrackingControllerProps,
  SendEventOptions,
  DatadogEventData,
  KakaoPixelMethods,
  KakaoPixelEventParams,
  MetaPixelEventNames,
  MetaPixelParams,
  NaverPixelEventParams
} from './types';

export class EventTrackingController {
  private mixpanel?: MixpanelService;
  private airbridge?: AirbridgeService;
  private metaPixel?: MetaPixelService;
  private tiktokPixel?: TiktokPixelService;
  private naverPixel?: NaverPixelService;
  private kakaoPixel?: KakaoPixelService;
  private googleAnalytics?: GoogleAnalyticsService;
  private datadog?: DatadogService;

  constructor(initializeParams: EventTrackingControllerProps) {
    this.init(initializeParams);
  }
  init({
    appId,
    clientId,
    canInitializeMixpanel,
    mixpanelKey,
    mixpanelConfig,
    enableLoginStatusProperty,
    landingId,
    airbridgeAppId,
    airbridgeWebToken,
    airbridgeConfig,
    metaPixelId,
    tiktokPixelId,
    naverPixelId,
    kakaoPixelId,
    googleTrackingIds,
    datadogConfig,
    appVersion
  }: EventTrackingControllerProps) {
    if (IS_IFRAME) return;
    if (!this.mixpanel && mixpanelKey)
      this.mixpanel = new MixpanelService({ appId, clientId, mixpanelKey, mixpanelConfig, enableLoginStatusProperty, landingId, canInitializeMixpanel });
    if (!this.airbridge && airbridgeAppId && airbridgeWebToken)
      this.airbridge = new AirbridgeService({ appId, clientId, airbridgeAppId, airbridgeWebToken, airbridgeConfig });
    if (!this.metaPixel && metaPixelId) this.metaPixel = new MetaPixelService({ appId, metaPixelId });
    if (!this.tiktokPixel && tiktokPixelId) this.tiktokPixel = new TiktokPixelService({ tiktokPixelId });
    if (!this.naverPixel && naverPixelId) this.naverPixel = new NaverPixelService({ naverPixelId });
    if (!this.kakaoPixel && kakaoPixelId) this.kakaoPixel = new KakaoPixelService({ kakaoPixelId });
    if (!this.googleAnalytics && googleTrackingIds) this.googleAnalytics = new GoogleAnalyticsService();
    if (!this.datadog && datadogConfig) this.datadog = new DatadogService({ appId, clientId, datadogConfig, appVersion });
  }

  getMixpanelDeviceId() {
    return this.mixpanel?.mixpanelDeviceId ?? '';
  }

  setCanInitializeMixpanel(canInitializeMixpanel: boolean) {
    this.mixpanel?.setCanInitializeMixpanel(canInitializeMixpanel);
  }

  setUser(params: SetUserParams) {
    if (IS_IFRAME) return;
    this.mixpanel?.setUser(params);
    this.airbridge?.setUser(params);
    this.metaPixel?.setUser(params);
    this.tiktokPixel?.setUser(params);
    this.datadog?.setUser(params);
  }

  sendEvent(eventName: string, properties?: SendEventProperties, options: SendEventOptions = {}) {
    if (IS_IFRAME) return;
    const targets = options.sendTargets ?? ['mixpanel'];
    let sentMixpanelEvent = undefined;
    let sentAirbridgeEvent = undefined;

    if (targets.includes('mixpanel')) sentMixpanelEvent = this.mixpanel?.sendEvent(eventName, properties);
    if (targets.includes('airbridge')) sentAirbridgeEvent = this.airbridge?.sendEvent(eventName, properties);
    if (targets.includes('tiktokPixel')) this.tiktokPixel?.sendEvent(eventName, properties);
    if (targets.includes('googleAnalytics')) this.googleAnalytics?.sendEvent(eventName, properties);
    if (targets.includes('datadog')) this.datadog?.sendEvent(eventName, properties ?? {});

    if ((!sentMixpanelEvent && !sentAirbridgeEvent) || process.env.NEXT_PUBLIC_ZENV === 'loc') return;
    requestPostFetch({
      url: process.env.NEXT_PUBLIC_MSK_API_SERVER ?? 'https://producer.dev.api.zent.kr/producer',
      data: {
        topic: `analytics-${process.env.NEXT_PUBLIC_ZENV === 'loc' ? 'dev' : process.env.NEXT_PUBLIC_ZENV}`,
        messages: [
          {
            value: {
              mixpanel: sentMixpanelEvent,
              airbridge: sentAirbridgeEvent
            }
          }
        ]
      }
    });
  }

  sendMetaPixelEvent(eventName: MetaPixelEventNames, eventParams?: MetaPixelParams) {
    this.metaPixel?.sendEvent(eventName, eventParams);
  }

  sendKakaoPixelEvent(methodName: KakaoPixelMethods, eventParams?: KakaoPixelEventParams) {
    this.kakaoPixel?.sendEvent(methodName, eventParams);
  }

  sendNaverPixelEvent(params: NaverPixelEventParams) {
    this.naverPixel?.sendEvent(params);
  }

  sendDatadogEvent(data: DatadogEventData) {
    this.datadog?.sendDatadogEvent(data);
  }

  unsetUser() {
    if (IS_IFRAME) return;
    this.mixpanel?.unsetUser();
    this.airbridge?.unsetUser();
    this.metaPixel?.unsetUser();
    this.tiktokPixel?.unsetUser();
    this.datadog?.unsetUser();
  }
}
